import * as Sentry from "@sentry/react";
import { useMutation } from "@tanstack/react-query";
import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { UploadSource } from "@rms-types/lib/s3/interface";

import { ServerError } from "../query";

import { axiosS3 } from "@/helpers";

export const getErrorMessage = (error: AxiosError<ServerError>, genericMessage: string) => {
  if (error.response && error.response.status >= 500) {
    return "Internal Server Error. OneComply has been notified";
  }

  return error.response?.data?.message || genericMessage;
};

export type CreateBulkSignedUrlPayload = {
  files: {
    trackingId: string;
    name: string;
  }[];
  path?: {
    suffix: string | null;
  };
  config?: AxiosRequestConfig<{ files: CreateBulkSignedUrlPayload["files"] }>;
  uploadSource: UploadSource;
};
export type CreateBulkSignedUrlResponse = {
  files: {
    name: string;
    trackingId: string;
    uploadLink: string;
  }[];
  failed_files: {
    name: string;
    trackingId: string;
    code: string;
    message: string;
  }[];
};

export type UploadFileToS3Payload = {
  uploadLink: string;
  result: null | string | ArrayBuffer;
  config?: AxiosRequestConfig<UploadFileToS3Payload["result"]>;
};
export type UploadFileToS3Response = void;

export function useS3({
  handleFileUploadProgress,
  handleFileUploadError,
  handleFileUploadComplete,
}: {
  handleFileUploadProgress: (trackingId: string, meta: { loaded: number; total: number }) => void;
  handleFileUploadError: (trackingId: string, errorMessage: string) => void;
  handleFileUploadComplete: (trackingId: string) => void;
}) {
  const { mutateAsync: createSignedFileURLs } = useMutation<
    CreateBulkSignedUrlResponse,
    AxiosError<ServerError>,
    CreateBulkSignedUrlPayload
  >({
    mutationFn: async ({ files, config, uploadSource, path }) => {
      const payload = { files, path };
      return (await axios.post(`/attachment/create-bulk-signed-url/${uploadSource}`, payload, config)).data;
    },
  });

  const { mutateAsync: uploadFileToS3 } = useMutation<
    UploadFileToS3Response,
    AxiosError<ServerError>,
    UploadFileToS3Payload
  >({ mutationFn: async ({ uploadLink, result, config }) => (await axiosS3.put(uploadLink, result, config)).data });

  return {
    /**
     * Create signed URLs for each file. These will be used to store the file in S3
     */
    signFileURLs: (
      files: CreateBulkSignedUrlPayload["files"],
      uploadSource: UploadSource,
      config?: CreateBulkSignedUrlPayload["config"],
      path?: {
        suffix: string | null;
      }
    ) => {
      return createSignedFileURLs({ files, uploadSource, config, path });
    },

    /**
     * Store a file in S3 using its respected signed URL
     */
    uploadFileToS3: async ({ file, trackingId }: { file: File; trackingId: string }, uploadLink: string) => {
      try {
        await new Promise((resolve, reject) => {
          const reader = new FileReader();

          reader.onload = async () => {
            try {
              const response = await uploadFileToS3({
                uploadLink,
                result: reader.result,
                config: {
                  headers: {
                    "Content-Type": file.type,
                    "X-Actual-Content-Length": file.size,
                    "content-disposition": "inline",
                  },

                  onUploadProgress: (event) => {
                    event.total && handleFileUploadProgress(trackingId, { loaded: event.loaded, total: event.total });
                  },
                  withCredentials: false,
                },
              });

              resolve(response);
            } catch (error) {
              reject(error);
            }
          };

          reader.onerror = reject;

          reader.readAsArrayBuffer(file);
        });

        return {
          trackingId,
          file,
          url: uploadLink.split("?")[0],
        };
      } catch (error) {
        const errorMessage = "Failed to upload file";
        Sentry.withScope((scope) => {
          scope.setExtras({
            file_name: file.name,
            file_size: file.size,
            error,
          });
          Sentry.captureMessage(errorMessage, "warning");
        });
        handleFileUploadError(trackingId, errorMessage);
      } finally {
        handleFileUploadComplete(trackingId);
      }
    },
  };
}
